import { useNavigate } from 'react-router-dom';
import { useSession } from './assets/auth/SessionContext';
import { Ticket, BarChart3, CalendarSearch, SmartphoneCharging } from 'lucide-react';

const quickAccessCards = [
    {
        id: "tickets",
        title: "Ticket Management",
        description: "View, search and manage all issued tickets across events.",
        icon: Ticket,
        iconBg: "linear-gradient(135deg, #2563eb, #1e3a5f)",
        path: "/ticket-management",
    },
    {
        id: "reports",
        title: "Ticket List Sales",
        description: "View and export a full history of all tickets sold across events.",
        icon: BarChart3,
        iconBg: "linear-gradient(135deg, #D72B63, #9333ea)",
        path: "/list-tickets",
    },
    {
        id: "events",
        title: "Events",
        description: "Browse and manage upcoming and past ticketed events.",
        icon: CalendarSearch,
        iconBg: "linear-gradient(135deg, #0ea5e9, #2563eb)",
        path: "/events",
    },
    {
        id: "sellers",
        title: "Sellers",
        description: "Manage seller accounts, devices and team assignments.",
        icon: SmartphoneCharging,
        iconBg: "linear-gradient(135deg, #10b981, #0ea5e9)",
        path: "/device-users",
    },
];

function getGreeting(): string {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
}

function formatDate(): string {
    return new Date().toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

export default function Home() {
    const { session } = useSession();
    const navigate = useNavigate();
    const greeting = getGreeting();
    const displayName = session?.user?.displayName ?? "";

    return (
        <div style={{ minHeight: "100vh", background: "#f1f5f9" }}>
            {/* ── Hero section ──────────────────────────────── */}
            <div
                style={{
                    background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 60%, #D72B63 100%)",
                    padding: "48px 40px",
                    position: "relative",
                    overflow: "hidden",
                    color: "#ffffff",
                }}
            >
                {/* Decorative circles */}
                <div
                    style={{
                        position: "absolute",
                        top: "-60px",
                        right: "-60px",
                        width: "280px",
                        height: "280px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.06)",
                        pointerEvents: "none",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        bottom: "-80px",
                        left: "30%",
                        width: "220px",
                        height: "220px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.05)",
                        pointerEvents: "none",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        top: "20px",
                        left: "-40px",
                        width: "160px",
                        height: "160px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.04)",
                        pointerEvents: "none",
                    }}
                />

                {/* Content */}
                <div style={{ position: "relative", zIndex: 1, maxWidth: "800px" }}>
                    {/* Date badge */}
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "rgba(255,255,255,0.18)",
                            borderRadius: "20px",
                            padding: "4px 14px",
                            fontSize: "13px",
                            fontWeight: 600,
                            marginBottom: "20px",
                            letterSpacing: "0.2px",
                        }}
                    >
                        {formatDate()}
                    </div>

                    {/* Greeting */}
                    <h1
                        style={{
                            fontSize: "32px",
                            fontWeight: 700,
                            margin: "0 0 8px 0",
                            lineHeight: 1.2,
                            letterSpacing: "-0.3px",
                        }}
                    >
                        {greeting}{displayName ? `, ${displayName}` : ""}
                    </h1>

                    {/* Sub-text */}
                    <p
                        style={{
                            fontSize: "16px",
                            margin: 0,
                            color: "rgba(255,255,255,0.78)",
                            fontWeight: 400,
                        }}
                    >
                        Welcome back to TDI Ticketing back office
                    </p>
                </div>
            </div>

            {/* ── Quick Access cards ────────────────────────── */}
            <div style={{ padding: "32px 40px 48px" }}>
                <h2
                    style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#1e293b",
                        margin: "0 0 20px 0",
                        letterSpacing: "-0.1px",
                    }}
                >
                    Quick Access
                </h2>

                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "20px",
                    }}
                >
                    {quickAccessCards.map((card) => {
                        const Icon = card.icon;
                        return (
                            <div
                                key={card.id}
                                onClick={() => navigate(card.path)}
                                style={{
                                    background: "#ffffff",
                                    borderRadius: "16px",
                                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                                    padding: "28px 24px",
                                    flex: "1 1 200px",
                                    maxWidth: "280px",
                                    cursor: "pointer",
                                    transition: "transform 0.18s ease, box-shadow 0.18s ease",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "14px",
                                }}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLDivElement).style.transform = "translateY(-3px)";
                                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 28px rgba(0,0,0,0.11)";
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 2px 16px rgba(0,0,0,0.06)";
                                }}
                            >
                                {/* Icon circle */}
                                <div
                                    style={{
                                        width: "48px",
                                        height: "48px",
                                        borderRadius: "50%",
                                        background: card.iconBg,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Icon size={22} color="#ffffff" strokeWidth={2} />
                                </div>

                                {/* Text */}
                                <div>
                                    <div
                                        style={{
                                            fontSize: "15px",
                                            fontWeight: 700,
                                            color: "#1e293b",
                                            marginBottom: "6px",
                                        }}
                                    >
                                        {card.title}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: "13px",
                                            color: "#64748b",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {card.description}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
